// HorarioSemanalEditor — editor de horário com 7 dias da semana + totalizador.
// horario: objeto { "0":{inicio,fim,almoco_min?}, ... } ou null/undefined
// onChange(novoHorario): chamado com novo objeto (ou null pra limpar tudo)
// metaHoras: jornada-alvo (44, 40, etc) — usada pra colorir o totalizador

import { useMemo } from 'react';

const DIAS = [
  { idx: 1, nome: 'Seg', completo: 'Segunda' },
  { idx: 2, nome: 'Ter', completo: 'Terça' },
  { idx: 3, nome: 'Qua', completo: 'Quarta' },
  { idx: 4, nome: 'Qui', completo: 'Quinta' },
  { idx: 5, nome: 'Sex', completo: 'Sexta' },
  { idx: 6, nome: 'Sáb', completo: 'Sábado' },
  { idx: 0, nome: 'Dom', completo: 'Domingo' },
];

function timeStrToMin(s) {
  if (!s) return 0;
  const [h, m] = s.split(':').map(Number);
  return h * 60 + (m || 0);
}
function formatHoras(min) {
  if (!min || min <= 0) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

export default function HorarioSemanalEditor({ horario = {}, onChange, metaHoras = 44 }) {
  const h = horario || {};

  function setDia(diaIdx, patch) {
    const novo = { ...h };
    const atual = novo[diaIdx] || {};
    const upd = { ...atual, ...patch };
    // Se inicio/fim vieram vazios, remove o dia (= folga)
    if (!upd.inicio && !upd.fim) {
      delete novo[diaIdx];
    } else {
      novo[diaIdx] = upd;
    }
    onChange(Object.keys(novo).length ? novo : null);
  }

  function toggleDia(diaIdx) {
    const novo = { ...h };
    if (novo[diaIdx]) {
      delete novo[diaIdx];
    } else {
      novo[diaIdx] = { inicio: '08:00', fim: '18:00', almoco_min: 60 };
    }
    onChange(Object.keys(novo).length ? novo : null);
  }

  // Cálculos
  const dadosDias = DIAS.map(dia => {
    const bloco = h[dia.idx];
    if (!bloco) return { ...dia, ativo: false, bruto: 0, liquido: 0 };
    const bruto = timeStrToMin(bloco.fim) - timeStrToMin(bloco.inicio);
    let almoco = bloco.almoco_min != null ? bloco.almoco_min : (bruto >= 360 ? 60 : 0);
    const liquido = Math.max(0, bruto - almoco);
    return { ...dia, ativo: true, bruto, liquido, almoco_min: almoco, ...bloco };
  });

  const totais = useMemo(() => {
    const bruto = dadosDias.reduce((s, d) => s + d.bruto, 0);
    const liquido = dadosDias.reduce((s, d) => s + d.liquido, 0);
    return { bruto, liquido };
  }, [dadosDias]);

  const diff = (totais.liquido - metaHoras * 60) / 60;
  const corStatus = Math.abs(diff) < 0.5 ? '#059669' : Math.abs(diff) < 2 ? '#d97706' : '#dc2626';
  const labelStatus = Math.abs(diff) < 0.5
    ? '✓ bate com a meta'
    : diff > 0
      ? `+${diff.toFixed(1)}h acima da meta`
      : `${diff.toFixed(1)}h abaixo da meta`;

  function copiarSegProDemaisUteis() {
    const seg = h[1];
    if (!seg) { alert('Configure a segunda primeiro'); return; }
    const novo = { ...h };
    for (const d of [2, 3, 4, 5]) novo[d] = { ...seg };
    onChange(novo);
  }

  return (
    <div className="horario-semanal-editor">
      <div className="hse-toolbar">
        <button type="button" className="btn small secondary" onClick={copiarSegProDemaisUteis}>
          Copiar segunda → ter/qua/qui/sex
        </button>
        <button type="button" className="btn small secondary" onClick={() => onChange(null)}>
          Limpar tudo
        </button>
      </div>

      <table className="hse-table">
        <thead>
          <tr>
            <th>Dia</th>
            <th>Trabalha</th>
            <th>Início</th>
            <th>Fim</th>
            <th>Almoço (min)</th>
            <th style={{ textAlign: 'right' }}>Líquido</th>
          </tr>
        </thead>
        <tbody>
          {dadosDias.map(d => (
            <tr key={d.idx} style={{ opacity: d.ativo ? 1 : 0.55 }}>
              <td><strong>{d.completo}</strong></td>
              <td>
                <input type="checkbox" checked={d.ativo} onChange={() => toggleDia(d.idx)} />
              </td>
              <td>
                <input type="time" value={d.inicio || ''} disabled={!d.ativo}
                       onChange={e => setDia(d.idx, { inicio: e.target.value })} />
              </td>
              <td>
                <input type="time" value={d.fim || ''} disabled={!d.ativo}
                       onChange={e => setDia(d.idx, { fim: e.target.value })} />
              </td>
              <td>
                <input type="number" min="0" max="180" step="15"
                       value={d.almoco_min != null ? d.almoco_min : ''}
                       disabled={!d.ativo}
                       placeholder={d.ativo && d.bruto >= 360 ? '60 (auto)' : '0'}
                       onChange={e => setDia(d.idx, { almoco_min: e.target.value === '' ? null : Number(e.target.value) })}
                       style={{ width: 70 }} />
              </td>
              <td style={{ textAlign: 'right', color: d.ativo ? '#1f2937' : '#9ca3af', fontWeight: d.ativo ? 600 : 400 }}>
                {d.ativo ? formatHoras(d.liquido) : 'folga'}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #1f2937', background: '#f9fafb' }}>
            <td colSpan={5} style={{ textAlign: 'right', padding: 12, fontSize: 13 }}>
              <span style={{ color: '#6b7280' }}>Bruto: {formatHoras(totais.bruto)} · Almoço descontado · </span>
              <strong>Total líquido:</strong>
            </td>
            <td style={{ textAlign: 'right', padding: 12, fontSize: 16, fontWeight: 700 }}>
              {formatHoras(totais.liquido)}
            </td>
          </tr>
          <tr style={{ background: '#f9fafb' }}>
            <td colSpan={6} style={{ textAlign: 'right', padding: '4px 12px 12px', fontSize: 12, color: corStatus, fontWeight: 600 }}>
              Meta: {metaHoras}h/sem · {labelStatus}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
